import React, { useEffect, useState, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtomValue } from 'jotai';
import {
  chargingAtom,
  toggleCharging,
  BuildingIDProps,
  chargerTimeoutAtom,
  chargerScaleModeAtom,
  chargerShowAvailableOnlyAtom,
  plugPowerHistoryAtom,
} from '../atoms/chargers';
import { CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { listDevices } from '../handlers/plugCommissionHandler';
import { fpmEndpointLink } from '../../../index';
import { buildChartOption, stateMeta } from './chargingChartConfig';
import { DateTime } from 'luxon';

const CHARGING_POWER_THRESHOLD_W = 50;
const CHARGING_STATE_VALUE = 3;

type LastChargeSession = {
  startMs: number;
  endMs: number | null;
};

/**
 * Read-only charging graph. Mirrors FPM's component but omits the
 * `PlugControlPanel`, plug on/off WebSocket overlay, and the
 * `usePlugBulkActions` controls. Live per-plug Matter power is still
 * polled for the tooltip overlay because that's purely informational.
 */
const ChargingGraph: React.FC<BuildingIDProps> = ({ BuildingID }) => {
  const chargingDataMap = useAtomValue(chargingAtom);
  const historyMap = useAtomValue(plugPowerHistoryAtom);
  const viewType = useAtomValue(toggleCharging);
  const showAvailableOnly = useAtomValue(chargerShowAvailableOnlyAtom);
  const chargerTimeout = useAtomValue(chargerTimeoutAtom);
  const [nPlugsConfig, setNPlugsConfig] = useState(0);
  const scaleMode = useAtomValue(chargerScaleModeAtom);
  const chartRef = useRef<ReactECharts>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerReady, setContainerReady] = useState(false);

  const syncChartWidthToContainer = useCallback((force = false) => {
    if (!chartRef.current || !containerRef.current) return;
    const chart = chartRef.current.getEchartsInstance();
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const chartWidth = chart.getWidth();
    const chartHeight = chart.getHeight();
    if (force || Math.abs(chartWidth - rect.width) > 2 || Math.abs(chartHeight - rect.height) > 2) {
      chart.resize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    }
  }, []);

  const buildingKey = BuildingID ? BuildingID.toLowerCase() : undefined;
  const chargingData = buildingKey ? chargingDataMap[buildingKey] : undefined;
  const isTimedOut = buildingKey ? chargerTimeout[buildingKey] : false;

  const { data: devices = [] } = useQuery({
    queryKey: ['fpmMatterDevicesGraph', BuildingID],
    queryFn: () => listDevices(BuildingID || 'all'),
    enabled: !!BuildingID,
  });
  const { data: chargerGraphSnapshot = [] } = useQuery({
    queryKey: ['fpmChargerGraphSlotCount', BuildingID],
    queryFn: async () => {
      const response = await axios.get<Array<{ NChans?: number; CPower?: unknown }>>(
        `${fpmEndpointLink}charger-graph`,
        { params: { BuildingID } }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!BuildingID,
  });

  const plugNumToReachable = useMemo(() => {
    const map: Record<number, boolean> = {};
    for (const d of devices) {
      if (d.PlugNum != null && typeof d.reachable === 'boolean') {
        map[d.PlugNum] = d.reachable;
      }
    }
    return map;
  }, [devices]);
  const plugNumToAvailable = useMemo(() => {
    const map: Record<number, boolean> = {};
    for (const d of devices) { if (d.PlugNum != null) map[d.PlugNum] = d.Available !== 0; }
    return map;
  }, [devices]);
  const assignedPlugNums = useMemo(() => {
    const set = new Set<number>();
    for (const d of devices) { if (d.PlugNum != null) set.add(d.PlugNum); }
    return set;
  }, [devices]);

  useEffect(() => {
    if (!BuildingID) return;
    const loadSlotCount = async () => {
      try {
        const response = await axios.get<{ nPlugs?: number; Nplugs?: number; nplugs?: number }>(
          `${fpmEndpointLink}setup-simulation`,
          { params: { BuildingID } }
        );
        const configuredPlugs = Number(response.data?.nPlugs ?? response.data?.Nplugs ?? response.data?.nplugs ?? 0);
        setNPlugsConfig(Number.isFinite(configuredPlugs) ? configuredPlugs : 0);
      } catch (err) {
        console.error('Failed to load setup slot count:', err);
      }
    };
    loadSlotCount();
  }, [BuildingID]);

  // Per-plug GET /api/plugs/devices/:nodeId/power polling was removed: V/A
  // now ride the chargerData WebSocket payload as CVoltage[]/CCurrent[].
  // Polling all plugs every 5 s through Chrome's per-origin socket cap
  // produced ERR_INSUFFICIENT_RESOURCES and a 503 wall when plugs were
  // offline, which prevented the chart from rendering at all on remote
  // (non-LAN) loads of the FPM API. See chargingChartConfig.ts for the new
  // tooltip wiring.

  const chartLayoutKey =
    chargingData == null
      ? ''
      : `${BuildingID ?? ''}|${Math.max(
          Array.isArray(chargingData.CPower) ? chargingData.CPower.length : 0,
          Array.isArray(chargingData.CState) ? chargingData.CState.length : 0,
          Number((chargingData as { NChans?: number }).NChans ?? 0)
        )}`;

  useLayoutEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setContainerReady(true);
      } else {
        const timer = setTimeout(() => {
          if (containerRef.current) {
            const { width: w, height: h } = containerRef.current.getBoundingClientRect();
            if (w > 0 && h > 0) setContainerReady(true);
          }
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, [chartLayoutKey, BuildingID]);

  useEffect(() => {
    const handleResize = () => syncChartWidthToContainer(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [syncChartWidthToContainer]);

  useEffect(() => {
    if (!containerRef.current || !containerReady) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0 && chartRef.current) syncChartWidthToContainer(true);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [containerReady, syncChartWidthToContainer]);

  useEffect(() => {
    if (chargingData && chartRef.current && containerReady) {
      const timer = setTimeout(() => syncChartWidthToContainer(true), 100);
      return () => clearTimeout(timer);
    }
  }, [chargingData, containerReady, syncChartWidthToContainer]);

  useEffect(() => {
    if (chargingData && chartRef.current && containerReady) {
      const timer = setTimeout(() => syncChartWidthToContainer(true), 100);
      return () => clearTimeout(timer);
    }
  }, [viewType, scaleMode, chargingData, containerReady, syncChartWidthToContainer]);

  useEffect(() => {
    if (!containerReady) return;
    const timers = [0, 50, 100, 200, 400, 800, 1200, 1800, 2500].map((ms) =>
      setTimeout(() => syncChartWidthToContainer(true), ms)
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [containerReady, BuildingID, chartLayoutKey, syncChartWidthToContainer]);

  useEffect(() => {
    if (chargingData == null) setContainerReady(false);
  }, [chargingData]);

  const powerSeries = Array.isArray(chargingData?.CPower) ? (chargingData?.CPower ?? []) : [];
  const energySeries = Array.isArray(chargingData?.CEnergy) ? (chargingData?.CEnergy ?? []) : [];
  const cData = viewType === 'CPower' ? powerSeries : energySeries;
  const cStateData = Array.isArray(chargingData?.CState) ? (chargingData?.CState ?? []) : [];

  const maxAssignedPlugNum = assignedPlugNums.size > 0 ? Math.max(...Array.from(assignedPlugNums)) : 0;
  const nChansFromPayload = Number((chargingData as { NChans?: number } | undefined)?.NChans ?? 0);
  const nChansFromSnapshot = chargerGraphSnapshot.reduce((max, row) => {
    const nChans = Number(row?.NChans ?? 0);
    const cPowerLen = Array.isArray(row?.CPower) ? row.CPower.length : 0;
    return Math.max(max, Number.isFinite(nChans) ? nChans : 0, cPowerLen);
  }, 0);
  const maxSlots = Math.max(
    cData.length, cStateData.length, maxAssignedPlugNum,
    nPlugsConfig, nChansFromSnapshot, Number.isFinite(nChansFromPayload) ? nChansFromPayload : 0
  );
  const allPlugNums = Array.from({ length: maxSlots }, (_, i) => i + 1);
  const visiblePlugNums = showAvailableOnly
    ? allPlugNums.filter((n) => assignedPlugNums.has(n) ? !!plugNumToAvailable[n] : false)
    : allPlugNums;
  const lastChargeByPlug = useMemo(() => {
    const historyData = buildingKey && Array.isArray(historyMap[buildingKey]) ? historyMap[buildingKey] : [];
    const latestByPlug = new Map<number, LastChargeSession>();
    const activeStartByPlug = new Map<number, number>();
    for (const row of historyData) {
      const ts = Number(row.TimestampMs ?? 0) || DateTime.fromSQL(row.Timestamp, { zone: 'utc' }).toMillis();
      for (let plugNum = 1; plugNum <= maxSlots; plugNum++) {
        const state = Number(row.CState?.[plugNum - 1]);
        const raw = row.CPower?.[plugNum - 1];
        const watts = Number(raw);
        const isChargingByState = Number.isFinite(state) && state === CHARGING_STATE_VALUE;
        const isChargingByPower = Number.isFinite(watts) && watts > CHARGING_POWER_THRESHOLD_W;
        const isCharging = isChargingByState || (!Number.isFinite(state) && isChargingByPower);
        if (isCharging) {
          if (!activeStartByPlug.has(plugNum)) activeStartByPlug.set(plugNum, ts);
          continue;
        }
        const startedAt = activeStartByPlug.get(plugNum);
        if (startedAt != null) {
          latestByPlug.set(plugNum, { startMs: startedAt, endMs: ts });
          activeStartByPlug.delete(plugNum);
        }
      }
    }
    activeStartByPlug.forEach((startMs, plugNum) => {
      latestByPlug.set(plugNum, { startMs, endMs: null });
    });
    return latestByPlug;
  }, [buildingKey, historyMap, maxSlots]);
  const formatSessionTime = useCallback((ms: number) => (
    DateTime.fromMillis(ms, { zone: 'America/New_York' }).toFormat('MM/dd h:mm a')
  ), []);
  const formatSessionRange = useCallback((session?: LastChargeSession) => {
    if (!session) return '--';
    const start = formatSessionTime(session.startMs);
    const end = session.endMs == null ? 'now' : formatSessionTime(session.endMs);
    return `${start} - ${end}`;
  }, [formatSessionTime]);
  const lastChargedRangeByPlug = useMemo(() => {
    const out: Record<number, string> = {};
    for (const plugNum of visiblePlugNums) {
      out[plugNum] = formatSessionRange(lastChargeByPlug.get(plugNum));
    }
    return out;
  }, [visiblePlugNums, lastChargeByPlug, formatSessionRange]);

  if (!chargingData) {
    if (isTimedOut) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center', padding: '20px' }}>
          <Typography variant="h6" sx={{ color: 'error.main', marginBottom: '10px' }}>
            No recent charger reporting
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Charger data has not been received recently for this building.
          </Typography>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
        <CircularProgress style={{ color: 'goldenrod' }} />
        <h3 style={{ color: 'grey', marginTop: '20px' }}>Waiting for data...</h3>
      </div>
    );
  }

  const option = buildChartOption({
    viewType, visiblePlugNums, cData, cStateData, chargingData,
    assignedPlugNums, plugNumToAvailable, plugNumToReachable, scaleMode,
    lastChargedRangeByPlug,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', flexShrink: 0, flexWrap: 'wrap', gap: '8px' }}>
        <Typography style={{ color: 'text.secondary' }}>Charger Status:</Typography>
        {stateMeta.map((state) => (
          <div key={state.label} style={{ margin: '0 4px', display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: state.color, marginRight: '5px' }}></div>
            <span style={{ color: 'text.primary' }}>{state.label}</span>
          </div>
        ))}
        <div style={{ margin: '0 4px', display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 20, height: 20, border: '2px dashed firebrick', opacity: 0.8, marginRight: 5 }}></div>
          <span style={{ color: 'text.primary' }}>Offline</span>
        </div>
      </div>
      <div ref={containerRef} style={{ flex: '1 1 auto', minHeight: 0, minWidth: 0, width: '100%', position: 'relative' }}>
        {containerReady && (
          <ReactECharts
            ref={chartRef}
            option={option}
            className="cancelSelectorName"
            style={{ height: '100%', width: '100%', minHeight: '400px' }}
            opts={{ renderer: 'canvas' }}
            notMerge={false}
            onChartReady={(chart) => {
              const resizeIfReady = () => {
                if (!containerRef.current) return;
                const { width, height } = containerRef.current.getBoundingClientRect();
                if (width > 0 && height > 0) chart.resize({ width: Math.floor(width), height: Math.floor(height) });
              };
              [0, 50, 100, 200, 400, 800, 1200].forEach((ms) => setTimeout(resizeIfReady, ms));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ChargingGraph;
